// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  parseCostDataUpdateErrorResponse,
  uploadCostDataUpdate,
} from '@/costs/api/upload-cost-data-update';
import { CsvImportFlow } from '@/csv-import/components/csv-import-flow';
import type { CsvImportTemplateColumn } from '@/csv-import/types';
import { buildUpdateResultsView } from '@/csv-import/update-results-view';
import { Alert, AlertTitle, Box } from '@mui/material';

const KEY_COLUMN_HEADER = 'Hardfin cost ID';

const COLUMNS_HINT =
  'Every included column must have a value in each row, except where noted';

const BLANK_CLEARS_AMOUNT = 'Blank clears the amount';
const BLANK_CLEARS_PERSON = 'Blank clears the person';
const BLANK_CLEARS_PROJECT = 'Blank clears the project';

const COST_UPDATE_COLUMNS: CsvImportTemplateColumn[] = [
  {
    header: KEY_COLUMN_HEADER,
    description: 'Copy from the cost export',
    alwaysIncluded: true,
  },
  {
    header: 'User email',
    description: 'Login email of an organization member',
    blankNote: BLANK_CLEARS_PERSON,
  },
  {
    header: 'Date',
    description: 'Day the cost was incurred, today or earlier (2026-03-25)',
  },
  {
    header: 'Category',
    description: 'Category label',
  },
  {
    header: 'Description',
    description: 'Text note about this cost',
  },
  {
    header: 'Project',
    description: 'Project name',
    blankNote: BLANK_CLEARS_PROJECT,
  },
  {
    header: 'Amount',
    description: 'Amount, such as 250.75',
    blankNote: BLANK_CLEARS_AMOUNT,
    children: [
      {
        header: 'Currency',
        description: 'ISO code for the amount, such as USD',
      },
    ],
  },
];

const COST_RECORD_NOUN = { singular: 'cost', plural: 'costs' };

interface CostDataUpdateUploadProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export function CostDataUpdateUpload(props: CostDataUpdateUploadProps) {
  return (
    <CsvImportFlow
      templateStep={{
        title: 'Template for updating cost data',
        columns: COST_UPDATE_COLUMNS,
        columnsHint: COLUMNS_HINT,
        infoAlert: (
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Important information about the template</AlertTitle>
            <Box component="ul" sx={{ pl: 3, mt: 1 }}>
              <li>
                This uploader <u>updates existing records</u>; it does not
                create them
              </li>
              <li>Hardfin cost ID must match an existing cost</li>
              <li>Only the columns included in the upload file are updated</li>
              <li>An amount is only applied in a stated currency</li>
            </Box>
          </Alert>
        ),
        fileNamePrefix: 'cost_data_update_template',
      }}
      uploadStep={{
        dropZoneTitle: 'Upload a CSV file with your cost data updates',
        recordNoun: COST_RECORD_NOUN,
        warningAlert: (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Warning about cost data updates</AlertTitle>
            <Box
              component="ul"
              sx={{ pl: 3, mt: 2, mb: 1, '& li + li': { mt: 0.5 } }}
            >
              <li>This upload updates existing costs only</li>
              <li>
                Blank person, project, and amount cells clear those fields
              </li>
              <li>Delete a column to leave that field unchanged</li>
              <li>No changes are applied unless every row is valid</li>
            </Box>
          </Alert>
        ),
      }}
      uploadFile={uploadCostDataUpdate}
      parseErrorResponse={parseCostDataUpdateErrorResponse}
      supportErrorMessage="An error occurred while uploading cost data."
      buildResultsView={(result) =>
        buildUpdateResultsView(result, {
          keyColumnHeader: KEY_COLUMN_HEADER,
          recordNoun: COST_RECORD_NOUN,
          dataLabel: 'cost data',
        })
      }
      onSuccess={props.onSuccess}
      onClose={props.onClose}
    />
  );
}
